import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Users, BookOpen, BarChart3, ArrowRight, Zap, FileText, TrendingUp } from 'lucide-react';
import FeaturedEmployers from '../components/FeaturedEmployers';
import Testimonials from '../components/Testimonials';
import Logo from '../components/Logo';
import { AuthContext } from '../context/AuthContext';

export default function Home() {
  const { user } = useContext(AuthContext);
  const employerCtaPath = !user
    ? '/login?type=employer'
    : user.user_type === 'employer'
      ? '/post-job'
      : '/register?type=employer';

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white py-16 sm:py-24 md:py-32 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920')] bg-cover bg-center mix-blend-overlay"></div>
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="flex justify-center mb-4 sm:mb-6">
            <Logo size={170} className="sm:w-44 sm:h-44 md:w-48 md:h-48" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight px-2">Connecting Africa's Best Talent with Leading Employers</h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-3 sm:mb-4 text-blue-100 px-4">
            Matching exceptional professionals with employers of choice across key sectors
          </p>
          <p className="text-sm sm:text-base md:text-lg mb-6 sm:mb-10 text-blue-200 italic font-semibold">
            Connecting Talent. Empowering Futures.
          </p>
          <div className="flex flex-col sm:flex-row md:flex-row justify-center gap-3 sm:gap-4 px-4">
            <Link to={employerCtaPath} className="bg-white text-blue-600 px-6 sm:px-8 md:px-10 py-3 sm:py-4 rounded-lg font-semibold hover:bg-blue-50 transition flex items-center justify-center space-x-2 text-base sm:text-lg shadow-xl">
              <Briefcase size={20} className="sm:w-6 sm:h-6" />
              <span>Employer looking for Talent - Get in touch</span>
            </Link>
            <Link to="/jobs" className="border-2 border-white text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center space-x-2 text-base sm:text-lg shadow-xl">
              <Users size={20} className="sm:w-6 sm:h-6" />
              <span>Talent looking for a new career opportunity - Browse available roles</span>
            </Link>
          </div>
        </div>
      </section>

      <FeaturedEmployers />
      <Testimonials />

      {/* Why Talent Link - Company Mission */}
      <section className="py-20 px-4 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16">Why Talent Link?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20 hover:bg-white/15 transition">
              <div className="text-4xl mb-4">🚀</div>
              <h3 className="text-2xl font-bold mb-4">Why Talent Link?</h3>
              <p className="text-lg text-blue-100">
                We build careers, strengthen organisations, and drive Zimbabwe's economic growth.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20 hover:bg-white/15 transition">
              <div className="text-4xl mb-4">🤝</div>
              <h3 className="text-2xl font-bold mb-4">Your Strategic Partner</h3>
              <p className="text-lg text-blue-100">
                We help you secure top-tier talent that drives business results.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20 hover:bg-white/15 transition">
              <div className="text-4xl mb-4">🌍</div>
              <h3 className="text-2xl font-bold mb-4">Serving International Employers</h3>
              <p className="text-lg text-blue-100">
                End-to-end recruitment solutions for foreign companies hiring Zimbabwean talent.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20 hover:bg-white/15 transition">
              <div className="text-4xl mb-4">🌉</div>
              <h3 className="text-2xl font-bold mb-4">A Bridge for Zimbabweans</h3>
              <p className="text-lg text-blue-100">
                Connecting diaspora professionals with local opportunities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sectors We Serve Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4">Sectors We Serve</h2>
          <p className="text-center text-gray-600 mb-16 text-lg">Click any sector to view available vacancies</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Mining & Quarrying', 'Agriculture', 'Banking & Finance Services', 'Human Resources, Education, Legal, IT & Consultancy', 'Marketing', 'Forestry & Fishing', 'Engineering & Industrial', 'Construction', 'Energy', 'Wholesale & Retail Trade', 'Transport & Logistics', 'Procurement & Supply Chain', 'Administration & Support Services', 'Parastatals & NECs', 'Other Sectors'].map((sector, idx) => (
              <Link 
                key={idx} 
                to={`/jobs?category=${encodeURIComponent(sector)}`}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition border-l-4 border-blue-600 hover:border-blue-800 hover:bg-blue-50 cursor-pointer group"
              >
                <h3 className="text-lg font-semibold text-gray-800 group-hover:text-blue-700 transition flex items-center justify-between">
                  {sector}
                  <ArrowRight size={20} className="text-blue-600 group-hover:translate-x-1 transition-transform" />
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4">Our Services</h2>
          <p className="text-center text-gray-600 mb-16 text-lg">Comprehensive recruitment solutions tailored to your needs</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <Users className="text-blue-600" size={36} />,
                title: 'Executive & Professional Recruitment',
                description: 'Top-tier talent acquisition for leadership and management positions across all sectors.'
              },
              {
                icon: <Briefcase className="text-green-600" size={36} />,
                title: 'Skilled & Semi-Skilled Workforce',
                description: 'Technical and specialized professionals for diverse industry requirements.'
              },
              {
                icon: <BarChart3 className="text-purple-600" size={36} />,
                title: 'Mining & Industrial Sector',
                description: 'Specialized recruitment for Mining, Energy, Construction, and Industrial sectors.'
              },
              {
                icon: <BookOpen className="text-orange-600" size={36} />,
                title: 'HR Advisory & Consulting',
                description: 'Strategic HR advisory, talent consulting, and organizational development.'
              },
              {
                icon: <Zap className="text-red-600" size={36} />,
                title: 'Screening & Due Diligence',
                description: 'Comprehensive background checks, verification, and workforce screening services.'
              },
              {
                icon: <ArrowRight className="text-indigo-600" size={36} />,
                title: 'Talent Outsourcing',
                description: 'Contract staffing and flexible workforce solutions for project-based needs.'
              },
              {
                icon: <FileText className="text-teal-600" size={36} />,
                title: 'Market Surveys',
                description: 'In-depth market research and salary benchmarking for informed decisions.'
              },
              {
                icon: <TrendingUp className="text-pink-600" size={36} />,
                title: 'Career Guidance',
                description: 'Professional career counseling and development support for job seekers.'
              }
            ].map((service, idx) => (
              <div key={idx} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-1 border border-gray-100">
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-blue-50 py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            {[
              { number: '5000+', label: 'Job Seekers' },
              { number: '500+', label: 'Employers' },
              { number: '2000+', label: 'Jobs Posted' },
              { number: '85%', label: 'Success Rate' }
            ].map((stat, idx) => (
              <div key={idx}>
                <p className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</p>
                <p className="text-gray-600 font-semibold">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Whether you're seeking top talent or your next career opportunity, Talent Link is here to connect you with success.
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-4">
            <Link to={employerCtaPath} className="bg-white text-blue-600 px-10 py-4 rounded-lg font-semibold hover:bg-blue-50 transition flex items-center justify-center space-x-2 text-lg shadow-xl">
              <Briefcase size={20} />
              <span>For Employers</span>
              <ArrowRight size={20} />
            </Link>
            <Link to="/jobs" className="border-2 border-white text-white px-10 py-4 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center space-x-2 text-lg shadow-xl">
              <Users size={20} />
              <span>For Job Seekers</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
